import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo, contactReasons } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    reason: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      reason: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        sonnerToast.success('Message Sent!', {
          description: response.data.message,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          reason: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      
      const errorMessage = error.response?.data?.detail || 
                          'An error occurred while sending your message. Please try again.';
      
      sonnerToast.error('Error', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO 
        title="Contact Us"
        description="Contact Savanna Skies for aircraft leasing and financing inquiries. Serving airlines across South Africa, Kenya, Nigeria, and the United Kingdom."
        keywords="contact Savanna Skies, aircraft leasing inquiry, aviation finance contact, aircraft lease quote"
        canonicalUrl="/contact"
      />
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">Contact Us</h1>
          <p className="page-subtitle">
            Let's discuss how Savanna Skies can support your aviation needs
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-container">
          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <Card className="contact-form-card">
              <CardContent className="contact-form-content">
                <h2 className="contact-form-title">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="name" className="form-label">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Your full name"
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="email" className="form-label">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="your.email@company.com"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="company" className="form-label">Company</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Your airline or organization"
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="phone" className="form-label">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="reason" className="form-label">Inquiry Type *</Label>
                    <Select onValueChange={handleSelectChange} value={formData.reason} required>
                      <SelectTrigger className="form-select">
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                      <SelectContent>
                        {contactReasons.map((reason, index) => (
                          <SelectItem key={index} value={reason}>
                            {reason}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="form-group">
                    <Label htmlFor="message" className="form-label">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      className="form-textarea"
                      placeholder="Tell us about your aircraft leasing or financing needs..."
                      rows={6}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="btn-primary btn-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send size={18} className="icon-right" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="contact-info-wrapper">
            <div className="contact-info-section">
              <h2 className="contact-info-title">Get In Touch</h2>
              <p className="contact-info-description">
                Our team is ready to discuss your aircraft leasing and financing requirements.
              </p>

              <div className="contact-info-items">
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <Mail size={24} />
                  </div>
                  <div className="contact-info-details">
                    <div className="contact-info-label">Email</div>
                    <div className="contact-info-value">{companyInfo.email}</div>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <Phone size={24} />
                  </div>
                  <div className="contact-info-details">
                    <div className="contact-info-label">Phone</div>
                    <div className="contact-info-value">{companyInfo.phone}</div>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="contact-info-details">
                    <div className="contact-info-label">Address</div>
                    <div className="contact-info-value">{companyInfo.address}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-response-section">
              <h3 className="contact-response-title">Response Time</h3>
              <p className="contact-response-text">
                We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;